import { useEffect, useState, useCallback } from 'react';
import { View, StyleSheet, Pressable, LayoutChangeEvent } from 'react-native';
import { LineChart, Text, Icon, theme } from '@socrait/react';

import { Loading } from '~/components/Loading';
import { ErrorBox } from '~/components/ErrorBox';
import { Select } from '~/components/Select';
import { Tooltip } from '~/components/Tooltip';
import { AccordionBase } from '~/components/Accordion';
import { useStudentBehaviorReportQuery, useStudentSummaryMutation, type Timeframe } from '~/services/studentData';
import { studentDataStore } from '~/store/studentData/studentDataStore';
import { useActiveStudentContext } from '~/hooks/studentData/useActiveStudentContext';
import { useUserProfile } from '~/services/useUserProfileQuery';

import { TIMEFRAME_OPTIONS, SPARK_RESTRICTED_TIMEFRAMES, getTimeframeLabel } from './sharedFilters';
import { SparkTooltipContent } from './sparkTooltipContent';

const SERIES_COLORS = {
  praises: theme.colors.lightPurple,
  warnings: theme.colors.yellow,
  absent: theme.colors.navy,
};

const SERIES_ICONS = {
  praises: 'star' as const,
  warnings: 'hexagon' as const,
  absent: 'square' as const,
};

const SERIES_TYPES = {
  praises: 'line' as const,
  warnings: 'line' as const,
  absent: 'scatter' as const,
};

const BASELINE_ONLY = {
  absent: true,
};

export function OverviewTab() {
  const { student, studentId, periodId, periodCreatedAt } = useActiveStudentContext();
  const timeframe = studentDataStore((s) => s.timeframe);
  const setTimeframe = studentDataStore((s) => s.setTimeframe);
  const [chartContainerHeight, setChartContainerHeight] = useState(0);
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const { user } = useUserProfile();
  const isSpark = user?.isSpark ?? false;

  const {
    data: chartData,
    status: chartStatus,
    isFetching: chartFetching,
  } = useStudentBehaviorReportQuery({
    studentId,
    periodId,
    periodCreatedAt,
    timeframe,
  });

  const { mutate: generateSummary, data: summaryData, status: summaryStatus } = useStudentSummaryMutation();

  useEffect(() => {
    if (!studentId) return;
    generateSummary({
      studentId,
      periodId,
      periodCreatedAt,
      timeframe,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, timeframe]);

  const handleChartContainerLayout = useCallback((e: LayoutChangeEvent) => {
    setChartContainerHeight(e.nativeEvent.layout.height);
  }, []);

  const timeframeLabel = getTimeframeLabel(timeframe, { case: 'sentence' });

  const summaryContent = (
    <View style={styles.summaryContent}>
      {summaryStatus === 'pending' && <Loading backgroundColor="transparent" />}
      {summaryStatus === 'error' && <ErrorBox text="Failed to load summary. Please close this window and try again." />}
      {(summaryStatus === 'success' || summaryStatus === 'idle') && summaryData && (
        <Text style={styles.summaryText}>{summaryData.summary}</Text>
      )}
    </View>
  );

  const expandButton = (
    <Pressable onPress={() => setSummaryCollapsed(false)}>
      <Icon type="downChevron" width={16} height={10} />
    </Pressable>
  );

  return (
    <View style={styles.container}>
      <View style={styles.chartContainer} onLayout={handleChartContainerLayout}>
        <View style={styles.chartControls}>
          <Select
            value={timeframe}
            onChange={(v) => setTimeframe(v as Timeframe)}
            options={TIMEFRAME_OPTIONS}
            height={40}
            disabledValues={isSpark ? SPARK_RESTRICTED_TIMEFRAMES : undefined}
          />
          {isSpark && <Tooltip tooltipContent={<SparkTooltipContent />} />}
        </View>
        {(chartStatus === 'pending' || chartFetching) && <Loading backgroundColor="transparent" />}
        {chartStatus === 'error' && !chartFetching && (
          <View style={styles.errorContainer}>
            <ErrorBox
              text="Failed to load chart. Please close this window and try again."
              containerStyle={styles.errorBox}
            />
          </View>
        )}
        {chartStatus === 'success' && !chartFetching && chartData && chartContainerHeight > 0 && (
          <LineChart
            data={chartData}
            title={`${student?.voiceName}'s behavior (${timeframeLabel})`}
            showTitle={false}
            colors={SERIES_COLORS}
            icons={SERIES_ICONS}
            seriesTypes={SERIES_TYPES}
            baselineOnly={BASELINE_ONLY}
            height={chartContainerHeight}
          />
        )}
      </View>
      <AccordionBase
        title={`${student?.voiceName}'s summary (${timeframeLabel})`}
        icon={<Icon type="document" />}
        content={summaryContent}
        rightControls={expandButton}
        isCollapsed={summaryCollapsed}
        onToggleCollapse={setSummaryCollapsed}
        noContentBorder={true}
        containerStyle={styles.summaryContainer}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing.xlarge * 2,
    paddingBottom: theme.spacing.xxlarge,
  },
  chartContainer: {
    flex: 1,
    position: 'relative',
  },
  chartControls: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.small,
  },
  summaryContainer: {
    backgroundColor: theme.colors.lightGray,
    borderWidth: 0,
  },
  summaryContent: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.large,
    padding: theme.spacing.large,
  },
  summaryText: {
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.small,
  },
  errorContainer: {
    flex: 1,
    paddingTop: 40 + theme.spacing.xlarge,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorBox: {
    width: '100%',
  },
});
